import React, { useEffect, useRef, useState } from "react"
import {
  Badge,
  Button,
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  Row,
  Spinner,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from "reactstrap"
import CellEditor from "./CellEditor"
import LoadingOverlay from "react-loading-overlay"
import { useNavigate } from "react-router-dom"
import Cookies from "js-cookie"

// Long values (ids, urls) would otherwise overflow the metadata modal, since
// <pre> does not wrap by default.
const PRE_STYLE = { whiteSpace: "pre-wrap", wordBreak: "break-word" }

const isPrimitive = v => v === null || typeof v !== "object"

// A list of flat dicts (e.g. RETAILLINK related_stop_locs over business_unit +
// node) reads far better as a small table than as raw JSON. Anything deeper
// falls through to JSON so no shape is ever hidden or lossily summarised.
const isFlatDictArray = v =>
  Array.isArray(v) &&
  v.length > 0 &&
  v.every(
    row =>
      row !== null &&
      typeof row === "object" &&
      !Array.isArray(row) &&
      Object.values(row).every(isPrimitive)
  )

const MetaValue = ({ value }) => {
  if (isPrimitive(value)) return <>{String(value)}</>

  if (Array.isArray(value) && value.every(isPrimitive)) {
    return value.length > 0 ? (
      <>{value.map(String).join(", ")}</>
    ) : (
      <span className="text-muted">&mdash;</span>
    )
  }

  if (isFlatDictArray(value)) {
    const cols = [...new Set(value.flatMap(row => Object.keys(row)))]
    return (
      <table className="table table-sm table-borderless mb-0">
        <thead>
          <tr>
            {cols.map(c => (
              <th key={c} className="text-muted fw-normal small ps-0">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {value.map((row, i) => (
            <tr key={i}>
              {cols.map(c => (
                <td key={c} className="small ps-0">
                  {row[c] == null ? "" : String(row[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )
  }

  return (
    <pre className="mb-0 small" style={PRE_STYLE}>
      {JSON.stringify(value, null, 2)}
    </pre>
  )
}

const LocationIdCell = props => {
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [singlebtn, setSinglebtn] = useState(false)
  const api_url = process.env.REACT_APP_API_URL

  const navigate = useNavigate()

  const [copied, setCopied] = useState(false)
  const [showMeta, setShowMeta] = useState(false)
  const copiedTimer = useRef(null)

  useEffect(() => () => clearTimeout(copiedTimer.current), [])

  // qd_location.id — useful when cross-referencing a row against the DB or a
  // support ticket, so it is surfaced in the row menu instead of a grid column.
  const qdLocationId = props.row.original.quedLocationId

  const mLocId = props.row.original.m_loc_id

  // qd_location.adtnl_attrbs - appointment-system specific fields. psycopg2
  // decodes jsonb before it reaches us, so this is normally already an object;
  // parse defensively in case it ever arrives as a JSON string.
  const rawAttrs = props.row.original.adtnl_attrbs
  let attrs = rawAttrs
  if (typeof rawAttrs === "string") {
    try {
      attrs = JSON.parse(rawAttrs)
    } catch (err) {
      // Not JSON - keep the raw string so it is shown rather than hidden.
      attrs = rawAttrs
    }
  }
  const isPlainAttrObject =
    attrs !== null && typeof attrs === "object" && !Array.isArray(attrs)
  const attrEntries = isPlainAttrObject ? Object.entries(attrs) : []
  // adtnl_attrbs is written as an object of field-name -> value, but render
  // anything else verbatim rather than reporting "no attributes" over real data.
  const attrFallback =
    !isPlainAttrObject && attrs != null && attrs !== "" ? attrs : null

  // Legacy path: the async clipboard API is unavailable outside secure contexts
  // and can also reject (permission denied, document not focused).
  const copyViaTextarea = text => {
    const ta = document.createElement("textarea")
    ta.value = text
    ta.style.position = "fixed"
    ta.style.opacity = "0"
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand("copy")
    document.body.removeChild(ta)
    return ok
  }

  const copyQdLocationId = async () => {
    const text = String(qdLocationId ?? "")
    if (!text) return

    let ok = false
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
        ok = true
      }
    } catch (err) {
      // Swallowed on purpose - fall through to the legacy path below.
    }
    if (!ok) {
      try {
        ok = copyViaTextarea(text)
      } catch (err) {
        console.error("Failed to copy Qd Location Id", err)
      }
    }

    if (!ok) return
    setCopied(true)
    clearTimeout(copiedTimer.current)
    copiedTimer.current = setTimeout(() => setCopied(false), 1500)
  }

  const save = newValue => {
    setLoading(true)
  }

  function tog_center(status) {
    setShowModal(!showModal)
  }

  const refreshTable = () => {
    props.refreshTable()
  }

  const gotoEmailTemplate = appt_sys_ref => {
    var qdLocationId = props.row.original.quedLocationId
    var locationName = props.row.original.tLocationName
    if (appt_sys_ref == "EMAIL") {
      navigate("/email-template", {
        state: {
          m_loc_id: props.row.original.m_loc_id,
          locationId: qdLocationId,
          locName: locationName,
        },
      })
    }

    if (appt_sys_ref == 'OPENDOCK') {
      const encodedName = encodeURIComponent(locationName)
      navigate('/opendock-loc/config/'+ props.row.original.m_loc_id +'/' + encodedName)
    }
  }

  const gotoEmailAddressConfig = () => {
    var qdLocationId = props.row.original.quedLocationId
    var locationName = props.row.original.tLocationName
    const encodedName = encodeURIComponent(locationName)
    navigate(
      "/email-address-config/" +
        props.row.original.m_loc_id +
        "/" +
        encodedName
    )
  }

  const gotoAddNewLocation = appt_sys_ref => {
    var qdLocationId = props.row.original.quedLocationId
    var locationName = props.row.original.tLocationName
    if (appt_sys_ref == "EMAIL") {
      navigate("/new-location-email", {
        state: {
          m_loc_id: props.row.original.m_loc_id,
          locationId: qdLocationId,
          locName: locationName,
        },
      })
    }

    if (appt_sys_ref == 'OPENDOCK') {
      navigate('/opendock-loc/', {state: {m_loc_id: props.row.original.m_loc_id, locationId: qdLocationId, locName: locationName}})
    }
  }

  const remove = async () => {
    setLoading(true)
    try {
      const token = Cookies.get("idToken")
      var url = api_url

      if (props.row.original.locationId?.length > 0) {
        url = url + "locations/" + encodeURIComponent(props.value)
      } else {
        url = url + "email/templatemapping/" + props.row.original.m_loc_id
      }

      console.log(props.row)

      const config = {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          qdLocationId: props.row.original.quedLocationId,
        }),
        credentials: "include",
      }
      const response = await fetch(url, config)

      if (!response.ok) {
        if ([401, 403].includes(response.status)) {
          logout()
        }
        const res = await response.json()
        setLoading(false)
        throw new Error(res.message)
      }
      const res = await response.json()
      console.log("hello success" + res.message)
      props.editRow(null)
      setShowModal(false)
      setLoading(false)
      props.refreshTable()
    } catch (error) {
      console.error("Error:" + error)
      console.error(error.message)
    }
  }

  const showConfirm = () => {
    setShowModal(!showModal)
  }

  // Menu-item visibility, lifted out of the JSX so the divider below the
  // Qd Location Id row only renders when at least one action follows it.
  const isAdmin    = Cookies.get("role") == "admin"
  const isEmail    = props.row.original.appt_sys_ref == "EMAIL"
  const isOpenDock = props.row.original.appt_sys_ref == "OPENDOCK"
  const canConfigure = !!(props.value || props.row.original.stage_loc_id)

  const showEdit        = !!(props.value && props.value?.length > 0)
  const showAddManaged  = !props.value && !props.row.original.stage_loc_id && (isEmail || isOpenDock) && isAdmin
  const showAddGeneric  = !props.value && !isEmail && !isOpenDock
  const showDelete      = canConfigure
  const showTemplateCfg = (isEmail || isOpenDock) && canConfigure && isAdmin
  const showEmailCfg    = isEmail && canConfigure
  const showMetadata = !!(
    qdLocationId != null ||
    mLocId ||
    attrEntries.length > 0 ||
    attrFallback !== null
  )

  const hasActions =
    showEdit ||
    showAddManaged ||
    showAddGeneric ||
    showDelete ||
    showTemplateCfg ||
    showEmailCfg ||
    showMetadata

  return (
    <div>
      <Modal
        isOpen={showModal}
        toggle={() => {
          tog_center(null)
        }}
        centered
        size="md"
      >
        <LoadingOverlay active={loading} spinner>
          <div className="modal-header">
            <h5 className="modal-title mt-0">Confirmation</h5>
            <button
              type="button"
              onClick={() => {
                setmodal_center(false)
              }}
              className="close"
              data-dismiss="modal"
              aria-label="Close"
            >
              <span aria-hidden="true" onClick={() => setShowModal(false)}>
                &times;
              </span>
            </button>
          </div>

          <div className="modal-body">
            Are you sure you want to delete the location id{" "}
            <span className="text-primary">{props.value}</span> ?
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => remove()}
            >
              Delete
            </button>
            <button
              type="button"
              className="btn btn-secodnary"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </button>
          </div>
        </LoadingOverlay>
      </Modal>
      {/* Read-only metadata. No copy control by design - the values are plain
          selectable text so they can be copied with the keyboard. */}
      <Modal isOpen={showMeta} toggle={() => setShowMeta(false)} centered size="md">
        <div className="modal-header">
          <h5 className="modal-title mt-0">Location Metadata</h5>
          <button
            type="button"
            className="close"
            data-dismiss="modal"
            aria-label="Close"
            onClick={() => setShowMeta(false)}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div className="modal-body">
          <table className="table table-sm mb-0">
            <tbody>
              <tr>
                <th scope="row" className="text-muted fw-normal" style={{ width: "40%" }}>
                  Qd Location Id
                </th>
                <td>
                  {qdLocationId != null ? (
                    qdLocationId
                  ) : (
                    <span className="text-muted">&mdash;</span>
                  )}
                </td>
              </tr>
              <tr>
                <th scope="row" className="text-muted fw-normal">
                  Master Location Id
                </th>
                <td>{mLocId || <span className="text-muted">&mdash;</span>}</td>
              </tr>
              {attrEntries.map(([key, value]) => (
                <tr key={key}>
                  <th scope="row" className="text-muted fw-normal">
                    {key}
                  </th>
                  <td>
                    <MetaValue value={value} />
                  </td>
                </tr>
              ))}
              {attrFallback !== null && (
                <tr>
                  <th scope="row" className="text-muted fw-normal">
                    adtnl_attrbs
                  </th>
                  <td>
                    <MetaValue value={attrFallback} />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {attrEntries.length === 0 && attrFallback === null && (
            <p className="text-muted small mb-0 mt-2">
              No additional attributes for this location.
            </p>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowMeta(false)}
          >
            Close
          </button>
        </div>
      </Modal>
      {loading && (
        <div
          className="d-flex justify-content-center"
          style={{ height: "100%", backgroundColor: "#EEE" }}
        >
          <Spinner
            className="ms-2"
            color="primary"
            style={{
              position: "absolute",
              display: "flex",
              justifyContent: "center",
              zIndex: "100",
            }}
          />
        </div>
      )}
      {props.editingRow != props.row.id && (
        <Row>
          <Col md={8}>

            {props.row.original.appt_sys_ref == 'OPENDOCK' ? props.row.original.locationId : props.value} &nbsp;{" "}
            {props.row.original.has_stage && props.row.original.appt_sys_ref == 'EMAIL' && (
              <Badge color="secondary" className="me-3">
                Draft
              </Badge>
            )}
          </Col>
          <Col md={4} className="d-flex justify-content-end">
            <UncontrolledDropdown>
              <DropdownToggle className="card-drop" tag="a">
                <i className="mdi mdi-dots-horizontal font-size-18" />
              </DropdownToggle>
              <DropdownMenu  className="dropdown-menu-end">
                {qdLocationId != null && (
                  <>
                    <DropdownItem
                      toggle={false}
                      onClick={copyQdLocationId}
                      className="d-flex align-items-center justify-content-between"
                      title="Copy Qd Location Id"
                    >
                      <span className="me-3">
                        <span className="text-muted me-1">Qd Location Id:</span>
                        <span className="fw-semibold">{qdLocationId}</span>
                      </span>
                      <i
                        className={
                          copied
                            ? "mdi mdi-check font-size-16 text-success"
                            : "mdi mdi-content-copy font-size-16 text-primary"
                        }
                      />
                    </DropdownItem>
                    {hasActions && <DropdownItem divider />}
                  </>
                )}
                {showMetadata && (
                  <DropdownItem onClick={() => setShowMeta(true)}>
                    <i className="mdi mdi-information-outline font-size-16 text-info me-1" />{" "}
                    Show metaData
                  </DropdownItem>
                )}
                {showEdit && (
                  <DropdownItem onClick={() => props.editRow(props.row)}>
                    <i className="mdi mdi-pencil font-size-16 text-success me-1" />{" "}
                    Edit
                  </DropdownItem>
                )}
                {showAddManaged && (
                  <DropdownItem
                    onClick={() =>
                      gotoAddNewLocation(props.row.original.appt_sys_ref)
                    }
                  >
                    <i className="mdi mdi-plus font-size-16 text-primary me-1" />{" "}
                    Add
                  </DropdownItem>
                )}
                {showAddGeneric && (
                  <DropdownItem onClick={() => props.editRow(props.row)}>
                    <i className="mdi mdi-plus font-size-16 text-primary me-1" />{" "}
                    Add
                  </DropdownItem>
                )}
                {showDelete && (
                  <DropdownItem onClick={() => showConfirm()}>
                    <i className="mdi mdi-trash-can font-size-16 text-danger me-1" />{" "}
                    Delete
                  </DropdownItem>
                )}
                {showTemplateCfg && (
                  <DropdownItem
                    onClick={() =>
                      gotoEmailTemplate(props.row.original.appt_sys_ref)
                    }
                  >
                    <i className="mdi mdi-card-bulleted-settings-outline text-success me-1" />{" "}
                    Template Configuration
                  </DropdownItem>
                )}
                {showEmailCfg && (
                  <DropdownItem onClick={gotoEmailAddressConfig}>
                    <i className="mdi mdi-email-edit-outline text-primary me-1" />{" "}
                    Email Address Config
                  </DropdownItem>
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
            &nbsp;&nbsp;&nbsp;&nbsp;
            {/* {!props.value && <span>&nbsp;&nbsp;&nbsp;&nbsp;</span>} */}
          </Col>
        </Row>
      )}
      {props.editingRow == props.row.id && (
        <CellEditor
          value={props.value}
          editRow={props.editRow}
          onSave={save}
          row={props.row}
          refreshTable={refreshTable}
        />
      )}
    </div>
  )
}

export default LocationIdCell
